import React, { useState, useEffect } from 'react';
import {
  BookOpenIcon,
  MagnifyingGlassIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  DocumentTextIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

interface UserGuideViewerProps {
  className?: string;
}

export default function UserGuideViewer({ className = '' }: UserGuideViewerProps) {
  const [guideContent, setGuideContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [tableOfContents, setTableOfContents] = useState<Array<{ id: string; title: string; level: number }>>([]);

  useEffect(() => {
    loadGuideContent();
  }, []);

  const loadGuideContent = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Fetch the markdown file from the public directory
      const response = await fetch('/docs/Nutrition-Management-User-Guide.md');
      
      if (!response.ok) {
        throw new Error(`Failed to load user guide: ${response.status} ${response.statusText}`);
      }
      
      const content = await response.text();
      setGuideContent(content);
      
      // Extract table of contents
      const toc = extractTableOfContents(content);
      setTableOfContents(toc);
      
    } catch (err: any) {
      console.error('Error loading user guide:', err);
      setError(err.message || 'Failed to load user guide');
    } finally {
      setIsLoading(false);
    }
  };

  const extractTableOfContents = (content: string): Array<{ id: string; title: string; level: number }> => {
    const lines = content.split('\n');
    const toc: Array<{ id: string; title: string; level: number }> = [];
    
    lines.forEach(line => {
      const match = line.match(/^(#{1,6})\s+(.+)$/);
      if (match) {
        const level = match[1].length;
        const title = match[2];
        const id = title.toLowerCase()
          .replace(/[^\w\s-]/g, '')
          .replace(/\s+/g, '-')
          .trim();
        
        toc.push({ id, title, level });
      }
    });
    
    return toc;
  };

  const toggleSection = (sectionId: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const parseMarkdownToHtml = (markdown: string) => {
    let html = markdown;
    
    // Convert headers
    html = html.replace(/^### (.*$)/gim, '<h3 class="text-xl font-medium text-gray-800 mb-3 mt-6">$1</h3>');
    html = html.replace(/^## (.*$)/gim, '<h2 class="text-2xl font-semibold text-gray-800 mb-4 mt-8">$1</h2>');
    html = html.replace(/^# (.*$)/gim, '<h1 class="text-3xl font-bold text-slate-900 mb-6 pb-3 border-b border-gray-200">$1</h1>');
    
    // Convert bold text
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-slate-900">$1</strong>');
    
    // Convert italic text
    html = html.replace(/\*(.*?)\*/g, '<em class="italic text-slate-700">$1</em>');
    
    // Convert code blocks
    html = html.replace(/```([\s\S]*?)```/g, '<pre class="bg-gray-100 p-4 rounded-lg overflow-x-auto mb-4 text-sm"><code>$1</code></pre>');
    
    // Convert inline code
    html = html.replace(/`([^`]+)`/g, '<code class="bg-gray-100 px-2 py-1 rounded text-sm font-mono text-gray-800">$1</code>');
    
    // Convert unordered lists
    html = html.replace(/^\- (.+)$/gim, '<li class="ml-4">$1</li>');
    html = html.replace(/(<li.*<\/li>)/s, '<ul class="list-disc list-inside mb-4 space-y-2 text-slate-600">$1</ul>');
    
    // Convert ordered lists
    html = html.replace(/^\d+\. (.+)$/gim, '<li class="ml-4">$1</li>');
    
    // Convert paragraphs
    html = html.replace(/^(?!<[h1-6]|<ul|<ol|<li|<pre|<code)(.+)$/gim, '<p class="text-slate-600 mb-4 leading-relaxed">$1</p>');
    
    // Convert line breaks
    html = html.replace(/\n/g, '<br>');
    
    return html;
  };

  const filteredContent = searchTerm.trim() 
    ? guideContent.split('\n').filter(line => 
        line.toLowerCase().includes(searchTerm.toLowerCase())
      ).join('\n')
    : guideContent;

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center py-12 ${className}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading user guide...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-red-50 border border-red-200 rounded-lg p-6 ${className}`}>
        <div className="flex">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-3 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-red-800">Error Loading User Guide</h3>
            <p className="mt-1 text-sm text-red-700">{error}</p>
            <button
              onClick={loadGuideContent}
              className="mt-3 text-sm bg-red-100 hover:bg-red-200 text-red-800 px-3 py-1 rounded"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white shadow rounded-lg ${className}`}>
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <BookOpenIcon className="h-6 w-6 text-cyan-600 mr-2" />
            <h2 className="text-lg font-medium text-slate-900">Nutrition Management User Guide</h2>
          </div>
          
          {/* Search */}
          <div className="flex items-center w-64 rounded-md border border-gray-300 bg-white focus-within:ring-1 focus-within:ring-cyan-500 focus-within:border-cyan-500">
            <span className="pl-3 pr-2 flex items-center flex-shrink-0 text-slate-400" aria-hidden="true">
              <MagnifyingGlassIcon className="h-4 w-4" />
            </span>
            <input
              type="text"
              placeholder="Search guide..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 min-w-0 pr-3 py-2 border-0 bg-transparent text-sm focus:outline-none focus:ring-0"
            />
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Table of Contents Sidebar */}
        <div className="w-64 border-r border-gray-200 bg-slate-50/80 p-4 max-h-screen overflow-y-auto">
          <h3 className="text-sm font-medium text-slate-900 mb-3">Table of Contents</h3>
          <nav className="space-y-1">
            {tableOfContents.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`block w-full text-left px-2 py-1 text-sm rounded hover:bg-gray-200 ${
                  item.level === 1 ? 'font-medium text-slate-900' :
                  item.level === 2 ? 'ml-2 text-slate-700' :
                  'ml-4 text-slate-600'
                }`}
              >
                {item.title}
              </button>
            ))}
          </nav>
        </div>

        {/* Content Area */}
        <div className="flex-1 p-6 max-h-screen overflow-y-auto">
          {guideContent ? (
            <div className="prose max-w-none">
              <div 
                className="markdown-content"
                dangerouslySetInnerHTML={{ 
                  __html: parseMarkdownToHtml(searchTerm.trim() ? filteredContent : guideContent) 
                }}
              />
            </div>
          ) : (
            <div className="text-center py-12">
              <DocumentTextIcon className="mx-auto h-12 w-12 text-slate-400" />
              <h3 className="mt-2 text-sm font-medium text-slate-900">No content available</h3>
              <p className="mt-1 text-sm text-slate-500">
                The user guide content could not be loaded.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="px-6 py-3 border-t border-gray-200 bg-slate-50/80">
        <div className="flex items-center justify-between text-sm text-slate-600">
          <div className="flex items-center">
            <InformationCircleIcon className="h-4 w-4 mr-1" />
            <span>Comprehensive guide to the Nutrition Management module</span>
          </div>
          <div className="flex items-center">
            <CheckCircleIcon className="h-4 w-4 mr-1 text-green-500" />
            <span>Always up-to-date with latest features</span>
          </div>
        </div>
      </div>
    </div>
  );
} 